import React, { FC } from 'react';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import type { FundPerformanceAnalysisPayload } from '../../../../api/rs-hg/risk/fundPerformanceAnalysis.types';
import {
  percentageToTwoDecimalPlacesNoHundred,
  roundNumberToTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import GeneralCard from '../../../general/GeneralCard';
import CustomTable from '../../../tables/CustomTable';

export interface PerformanceReportTablesProps {
  payload: FundPerformanceAnalysisPayload;
  lookBackPeriod: string;
}

type PerformanceReportTableTypes = 'performance' | 'volatility' | 'sharpe';
interface PerformanceReportTableData {
  fund: string;
  month1: number;
  month3: number;
  month6: number;
  year1: number;
  year3: number | null;
}

function buildTableData(
  payload: FundPerformanceAnalysisPayload,
  tableType: PerformanceReportTableTypes,
  lookBackPeriod: string,
): PerformanceReportTableData[] {
  const volatilityData = payload.volatility_data;
  const cumulativeData = payload.cumulative_data;
  const riskFreeRate = payload.risk_free_rate;
  if (!volatilityData?.length || !cumulativeData?.length || !riskFreeRate?.length) {
    return [];
  }

  const sharpeRatioFactors = [3.4641, 2, 1.4142, 1, 0.574456265];
  const tableData: PerformanceReportTableData[] = [];

  for (let i = 0; i < volatilityData.length; i++) {
    if (tableType === 'performance') {
      const currentData = cumulativeData[i];
      tableData.push({
        fund: String(currentData[0]),
        month1: Number(currentData[1]),
        month3: Number(currentData[2]),
        month6: Number(currentData[3]),
        year1: Number(currentData[4]),
        year3: lookBackPeriod === '3' ? Number(currentData[5]) : null,
      });
    } else if (tableType === 'volatility') {
      const currentData = volatilityData[i];
      tableData.push({
        fund: String(currentData[0]),
        month1: Number(currentData[1]),
        month3: Number(currentData[2]),
        month6: Number(currentData[3]),
        year1: Number(currentData[4]),
        year3: lookBackPeriod === '3' ? Number(currentData[5]) : null,
      });
    } else if (tableType === 'sharpe') {
      const cumulative = cumulativeData[i];
      const volatility = volatilityData[i];
      tableData.push({
        fund: String(cumulative[0]),
        month1:
          sharpeRatioFactors[0] *
          ((Number(cumulative[1]) - riskFreeRate[1]) / Number(volatility[1])),
        month3:
          sharpeRatioFactors[1] *
          ((Number(cumulative[2]) - riskFreeRate[2]) / Number(volatility[2])),
        month6:
          sharpeRatioFactors[2] *
          ((Number(cumulative[3]) - riskFreeRate[3]) / Number(volatility[3])),
        year1:
          sharpeRatioFactors[3] *
          ((Number(cumulative[4]) - riskFreeRate[4]) / Number(volatility[4])),
        year3:
          lookBackPeriod === '3'
            ? sharpeRatioFactors[4] *
              ((Number(cumulative[5]) - riskFreeRate[5]) / Number(volatility[5]))
            : null,
      });
    }
  }

  return tableData;
}

function buildTableColumns(
  tableType: PerformanceReportTableTypes,
  lookBackPeriod: string,
): CustomColumn<PerformanceReportTableData>[] {
  const columns: CustomColumn<PerformanceReportTableData>[] = [
    {
      title: 'Fund',
      field: 'fund',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
      width: '33%',
    },
    {
      title: '1m',
      field: 'month1',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
      render: (rowData: PerformanceReportTableData) => {
        return tableType !== 'sharpe'
          ? percentageToTwoDecimalPlacesNoHundred(rowData.month1)
          : roundNumberToTwoDecimalPlaces(rowData.month1);
      },
    },
    {
      title: '3m',
      field: 'month3',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
      render: (rowData: PerformanceReportTableData) => {
        return tableType !== 'sharpe'
          ? percentageToTwoDecimalPlacesNoHundred(rowData.month3)
          : roundNumberToTwoDecimalPlaces(rowData.month3);
      },
    },
    {
      title: '6m',
      field: 'month3',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
      render: (rowData: PerformanceReportTableData) => {
        return tableType !== 'sharpe'
          ? percentageToTwoDecimalPlacesNoHundred(rowData.month6)
          : roundNumberToTwoDecimalPlaces(rowData.month6);
      },
    },
    {
      title: '1y',
      field: 'year1',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
      render: (rowData: PerformanceReportTableData) => {
        return tableType !== 'sharpe'
          ? percentageToTwoDecimalPlacesNoHundred(rowData.year1)
          : roundNumberToTwoDecimalPlaces(rowData.year1);
      },
    },
  ];

  if (lookBackPeriod === '3') {
    columns.push({
      title: '3y',
      field: 'year3',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
      render: (rowData: PerformanceReportTableData) => {
        return tableType !== 'sharpe'
          ? percentageToTwoDecimalPlacesNoHundred(rowData.year3)
          : roundNumberToTwoDecimalPlaces(rowData.year3);
      },
    });
  }

  return columns;
}

const PerformanceReportTables: FC<PerformanceReportTablesProps> = (props) => {
  const { payload, lookBackPeriod } = props;

  return (
    <>
      <GeneralCard
        cardStyle={{
          overflow: 'auto',
          marginBottom: '1.00rem',
          flexGrow: 0.33,
        }}
      >
        <CustomTable<PerformanceReportTableData>
          pdfTitle="Cumulative Fund Performance"
          title="Cumulative Fund Performance"
          columns={buildTableColumns('performance', lookBackPeriod)}
          id={'cumulative_fund_performance'}
          showToolbar
          data={buildTableData(payload, 'performance', lookBackPeriod)}
          multipleTablePdfParameters={{
            specifiedWidth: 5,
            tablesInRow: 1,
          }}
          options={{
            paging: false,
            search: false,
            exportButton: true,
            rowStyle: {
              marginLeft: 0,
            },
            sorting: false,
            draggable: false,
            toolbar: true,
          }}
          pdfSpecificYPosition={25}
          pdfSpecificXPosition={160}
        />
      </GeneralCard>
      <GeneralCard
        cardStyle={{
          overflow: 'auto',
          marginBottom: '1.00rem',
          flexGrow: 0.33,
        }}
      >
        <CustomTable<PerformanceReportTableData>
          pdfTitle="Cumulative Fund Volatility"
          title="Cumulative Fund Volatility"
          columns={buildTableColumns('volatility', lookBackPeriod)}
          id={'cumulative_fund_volatility'}
          showToolbar
          data={buildTableData(payload, 'volatility', lookBackPeriod)}
          multipleTablePdfParameters={{
            specifiedWidth: 5,
            tablesInRow: 1,
          }}
          options={{
            paging: false,
            search: false,
            exportButton: true,
            rowStyle: {
              marginLeft: 0,
            },
            sorting: false,
            draggable: false,
            toolbar: true,
          }}
          pdfSpecificYPosition={65}
          pdfSpecificXPosition={160}
        />
      </GeneralCard>
      <GeneralCard cardStyle={{ flexGrow: 0.33 }}>
        <CustomTable<PerformanceReportTableData>
          pdfTitle="Cumulative Anualised Fund Sharpe Ratio"
          title="Cumulative Anualised Fund Sharpe Ratio"
          columns={buildTableColumns('sharpe', lookBackPeriod)}
          id={'cumulative_fund_sharpe_ratio'}
          showToolbar
          data={buildTableData(payload, 'sharpe', lookBackPeriod)}
          multipleTablePdfParameters={{
            specifiedWidth: 5,
            tablesInRow: 1,
          }}
          options={{
            paging: false,
            search: false,
            exportButton: true,
            rowStyle: {
              marginLeft: 0,
            },
            sorting: false,
            draggable: false,
            toolbar: true,
          }}
          pdfSpecificYPosition={105}
          pdfSpecificXPosition={160}
        />
      </GeneralCard>
    </>
  );
};

export default PerformanceReportTables;
